import React, { useEffect, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import API from "../../services/api";
import ChartFilterBar, { ChartPeriod } from "./ChartFilterBar";

interface RevenueChartProps {
  /** When controlled externally (parent passes period in) */
  period?: ChartPeriod;
  /** Show the built-in filter bar — only used when period is NOT controlled externally */
  showFilter?: boolean;
  height?: number;
}

const RevenueChart = ({ period: externalPeriod, showFilter = true, height = 300 }: RevenueChartProps) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [localPeriod, setLocalPeriod] = useState<ChartPeriod>("month");

  const activePeriod = externalPeriod ?? localPeriod;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await API.get(`/admin/revenue-chart?period=${activePeriod}`);
        if (res.data?.success && res.data.data?.length) {
          setData(res.data.data);
        } else {
          setData([]);
        }
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activePeriod]);

  const displayData = loading ? [] : data.length ? data : [{ name: "No data", revenue: 0 }];

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 14 }}>
      {showFilter && !externalPeriod && (
        <ChartFilterBar value={localPeriod} onChange={setLocalPeriod} />
      )}
      <div style={{ flex: 1, minHeight: height, position: "relative" }}>
        {loading && (
          <div style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            background: "hsl(var(--card) / 0.6)", zIndex: 1, borderRadius: 12,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              border: "3px solid hsl(var(--border))",
              borderTopColor: "hsl(var(--primary))",
              animation: "spin 0.7s linear infinite",
            }} />
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={displayData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="hsl(var(--destructive))" stopOpacity={0.28} />
                <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="2 6" vertical={false} stroke="hsl(var(--border))" opacity={0.5} />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
              interval="preserveStartEnd"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
              tickFormatter={(v) => v === 0 ? "0" : `₹${(v / 1000).toFixed(0)}k`}
            />
            <Tooltip
              formatter={(value: any, name: any) => [
                `₹${Number(value).toLocaleString()}`,
                name === "revenue" ? "Revenue" : name === "failed" ? "Failed Payments" : String(name ?? "")
              ]}
              contentStyle={{
                borderRadius: "12px",
                border: "1px solid hsl(var(--border))",
                backgroundColor: "hsl(var(--card))",
                color: "hsl(var(--foreground))",
                fontSize: 13,
              }}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              name="revenue"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              fill="url(#colorRev)"
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Area
              type="monotone"
              dataKey="failed"
              name="failed"
              stroke="hsl(var(--destructive))"
              strokeWidth={2}
              fill="url(#colorFailed)"
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RevenueChart;
